import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  AccountTree,
  CalendarMonth,
  Clear,
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import SearchBar from '../common/SearchBar'
import apiService from '../../../../../services/ApiService'

const GCC_COUNTRIES = ['Bahrain', 'KSA', 'Kuwait', 'Oman', 'Qatar', 'UAE']

const GCC_FLAG_CODES = {
  Bahrain: 'bh',
  KSA: 'sa',
  Kuwait: 'kw',
  Oman: 'om',
  Qatar: 'qa',
  UAE: 'ae',
}

const STATUS_OPTIONS = [
  { label: 'Pending Review', value: 'PENDING_REVIEW' },
  { label: 'Rejected', value: 'REJECTED' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'All', value: '' },
]

const SORT_OPTIONS = [
  { label: 'Lowest Confidence First', value: 'newsConfidence:asc' },
  { label: 'Highest Confidence First', value: 'newsConfidence:desc' },
  { label: 'Newest First', value: 'pubDate:desc' },
  { label: 'Oldest First', value: 'pubDate:asc' },
]

const datePickerFieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.82rem',
    color: '#23252a',
    borderRadius: '8px',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#232536' },
    '&.Mui-focused fieldset': { borderColor: '#232536', borderWidth: '1.5px' },
    '&.Mui-error fieldset': { borderColor: '#e2e8f0' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#3a3e5a' },
}

const NewsFiltersBar = ({ filters, articles = [], onFilterChange, onFiltersChange, onSearch, onSort, onReset, hideStatusFilter = false }) => {
  // API-driven source options
  const [sourceOptions, setSourceOptions] = useState([])

  const selectedCountries = useMemo(
    () => (Array.isArray(filters.countries) ? filters.countries : []),
    [filters.countries],
  )
  const countriesKey = selectedCountries.join(',')

  useEffect(() => {
    let cancelled = false
    const params = {}
    if (selectedCountries.length) params.countries = selectedCountries
    if (filters.status) params.status = filters.status
    if (filters.confidenceMin !== undefined && filters.confidenceMin !== '') params.confidenceMin = filters.confidenceMin
    if (filters.confidenceMax !== undefined && filters.confidenceMax !== '') params.confidenceMax = filters.confidenceMax
    apiService.getNewsFilterOptions(params)
      .then((res) => { if (!cancelled) setSourceOptions(res?.sources ?? []) })
      .catch(() => {})
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countriesKey, filters.status, filters.confidenceMin, filters.confidenceMax])

  // Date bounds from articles
  const [overallMinDate, setOverallMinDate] = useState(null)
  const [overallMaxDate, setOverallMaxDate] = useState(null)

  useEffect(() => {
    if (!articles.length) return
    articles.forEach((a) => {
      if (!a.pubDate) return
      const t = new Date(a.pubDate)
      if (isNaN(t.getTime())) return
      const d = dayjs(t)
      setOverallMinDate((prev) => (!prev || d.isBefore(prev) ? d : prev))
      setOverallMaxDate((prev) => (!prev || d.isAfter(prev) ? d : prev))
    })
  }, [articles])

  // Date range dialog state
  const [dateRangeAnchor, setDateRangeAnchor] = useState(null)
  const [quickPreset, setQuickPreset] = useState(null)

  useEffect(() => {
    if (!filters.dateFrom && !filters.dateTo) setQuickPreset(null)
  }, [filters.dateFrom, filters.dateTo])

  const handleOpenDateRange = (e) => {
    setDateRangeAnchor(e.currentTarget)
  }

  const handleQuickPreset = (preset) => {
    const today = dayjs()
    setQuickPreset(preset)
    if (preset === 'today') {
      onFiltersChange({ dateFrom: today.startOf('day').format('YYYY-MM-DD'), dateTo: today.endOf('day').format('YYYY-MM-DD') })
    } else if (preset === '7days') {
      onFiltersChange({ dateFrom: today.subtract(6, 'day').startOf('day').format('YYYY-MM-DD'), dateTo: today.endOf('day').format('YYYY-MM-DD') })
    } else if (preset === '30days') {
      onFiltersChange({ dateFrom: today.subtract(29, 'day').startOf('day').format('YYYY-MM-DD'), dateTo: today.endOf('day').format('YYYY-MM-DD') })
    }
  }

  const selectedSources = Array.isArray(filters.source) ? filters.source : []

  const currentSort = `${filters.sortBy}:${filters.sortDir}`

  const handleSortChange = (val) => {
    const [sortBy, sortDir] = val.split(':')
    onSort(sortBy, sortDir)
  }

  const activeFilterCount = [
    filters.status !== 'PENDING_REVIEW',
    currentSort !== 'newsConfidence:asc',
    filters.countries?.length > 0,
    selectedSources.length > 0,
    filters.dateFrom,
    filters.dateTo,
  ].filter(Boolean).length

  return (
    <Box sx={{ mb: 2 }}>
      {/* Main filter row */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        {/* Search */}
        <SearchBar
          value={filters.search}
          onChange={() => { }}
          onSearch={onSearch}
          placeholder='Search news'
          sx={{ minWidth: { xs: '100%', sm: 280 } }}
        />

        {/* Status filter */}
        {!hideStatusFilter && (
        <FormControl size='small' sx={{ minWidth: 180 }}>
          <InputLabel id='news-status-filter-label'>Status</InputLabel>
          <Select
            labelId='news-status-filter-label'
            value={filters.status}
            label='Status'
            onChange={(e) => onFilterChange('status', e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        )}

        {/* Sort */}
        <FormControl size='small' sx={{ minWidth: 220 }}>
          <InputLabel id='news-sort-label'>Sort By</InputLabel>
          <Select
            labelId='news-sort-label'
            value={currentSort}
            label='Sort By'
            onChange={(e) => handleSortChange(e.target.value)}
          >
            {SORT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Country filter */}
        <FormControl size='small' sx={{ minWidth: 160 }}>
          <InputLabel id='news-country-filter-label'>Country</InputLabel>
          <Select
            labelId='news-country-filter-label'
            multiple
            value={filters.countries}
            label='Country'
            onChange={(e) => onFilterChange('countries', e.target.value)}
            renderValue={(selected) =>
              selected.length === 0 ? 'All Countries' : selected.join(', ')
            }
            MenuProps={{ disableScrollLock: true }}
          >
            {GCC_COUNTRIES.map((c) => (
              <MenuItem key={c} value={c} sx={{ py: 0.5 }}>
                <Checkbox
                  checked={filters.countries.includes(c)}
                  size='small'
                  sx={{ p: 0.5, '&.Mui-checked': { color: '#232536' } }}
                />
                {GCC_FLAG_CODES[c] && (
                  <img
                    loading='lazy'
                    width='18'
                    src={`https://flagcdn.com/w20/${GCC_FLAG_CODES[c]}.png`}
                    srcSet={`https://flagcdn.com/w40/${GCC_FLAG_CODES[c]}.png 2x`}
                    alt=''
                    style={{ marginRight: 6, flexShrink: 0, verticalAlign: 'middle' }}
                  />
                )}
                <ListItemText primary={c} primaryTypographyProps={{ fontSize: '0.85rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Source filter */}
        <FormControl size='small' sx={{ minWidth: 180 }} disabled={sourceOptions.length === 0}>
          <InputLabel id='news-source-filter-label'>Source</InputLabel>
          <Select
            labelId='news-source-filter-label'
            multiple
            value={selectedSources}
            label='Source'
            onChange={(e) => onFilterChange('source', e.target.value)}
            MenuProps={{ disableScrollLock: true }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AccountTree sx={{ fontSize: 15, color: '#64748b', flexShrink: 0 }} />
                <span>{selected.length === 1 ? selected[0] : `${selected.length} selected`}</span>
              </Box>
            )}
          >
            {sourceOptions.map((src) => (
              <MenuItem key={src} value={src} sx={{ py: 0.5 }}>
                <Checkbox
                  checked={selectedSources.includes(src)}
                  size='small'
                  sx={{ p: 0.5, '&.Mui-checked': { color: '#232536' } }}
                />
                <ListItemText primary={src} primaryTypographyProps={{ fontSize: '0.85rem' }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range Button */}
        <Button
          onClick={handleOpenDateRange}
          startIcon={<CalendarMonth sx={{ fontSize: 17 }} />}
          endIcon={dateRangeAnchor ? <KeyboardArrowUp sx={{ fontSize: 18 }} /> : <KeyboardArrowDown sx={{ fontSize: 18 }} />}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem',
            border: '1px solid',
            px: 1.8,
            height: '40px',
            whiteSpace: 'nowrap',
            borderColor: filters.dateFrom || filters.dateTo ? '#23252a' : '#e2e8f0',
            color: filters.dateFrom || filters.dateTo ? 'white' : '#23252a',
            backgroundColor: filters.dateFrom || filters.dateTo ? '#23252a' : 'white',
            '&:hover': {
              backgroundColor: filters.dateFrom || filters.dateTo ? '#374151' : '#f9fafb',
              borderColor: '#23252a',
            },
          }}
        >
          Date Range
        </Button>

        {/* Clear all */}
        {(activeFilterCount > 0 || filters.search) && (
          <Tooltip title='Reset all filters'>
            <Button
              startIcon={<Clear sx={{ fontSize: 17 }} />}
              onClick={onReset}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                height: '40px',
                px: 1.8,
                borderRadius: '8px',
                whiteSpace: 'nowrap',
                border: '1px solid #fca5a5',
                color: '#dc2626',
                backgroundColor: '#fff5f5',
                '&:hover': { backgroundColor: '#fee2e2', borderColor: '#dc2626' },
              }}
            >
              Clear All
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Date Range Dialog */}
      <Dialog
        open={Boolean(dateRangeAnchor)}
        onClose={() => setDateRangeAnchor(null)}
        maxWidth='xs'
        fullWidth
        disableScrollLock
        PaperProps={{ sx: { borderRadius: '12px', mx: { xs: 2, sm: 'auto' } } }}
      >
        <DialogTitle disableTypography sx={{ py: 1.5, px: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b' }}>
              Filter by Date Range
            </Typography>
            <IconButton
              onClick={() => setDateRangeAnchor(null)}
              size='small'
              sx={{ color: '#94a3b8', '&:hover': { backgroundColor: '#f1f5f9' } }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ pt: 2, px: 2.5, pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            {/* Quick Select panel */}
            <Box sx={{
              width: { xs: '100%', sm: 140 },
              flexShrink: 0,
              borderRight: { xs: 'none', sm: '1px solid #e2e8f0' },
              borderBottom: { xs: '1px solid #e2e8f0', sm: 'none' },
              pb: { xs: 1.5, sm: 0 },
              mb: { xs: 0.5, sm: 0 },
              pr: { xs: 0, sm: 1.5 },
            }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', mb: 1 }}>
                Quick Select
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, flexDirection: { xs: 'row', sm: 'column' } }}>
                {[
                  { label: 'Today', value: 'today' },
                  { label: 'Last 7 Days', value: '7days' },
                  { label: 'Last 30 Days', value: '30days' },
                ].map((p) => (
                  <Button
                    key={p.value}
                    onClick={() => handleQuickPreset(p.value)}
                    disableElevation
                    size='small'
                    sx={{
                      textTransform: 'none',
                      fontWeight: quickPreset === p.value ? 700 : 500,
                      fontSize: '0.82rem',
                      borderRadius: '6px',
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      backgroundColor: quickPreset === p.value ? '#23252a' : '#f1f5f9',
                      color: quickPreset === p.value ? 'white' : '#374151',
                      px: 1.5,
                      py: 0.6,
                      minWidth: 0,
                      '&:hover': { backgroundColor: quickPreset === p.value ? '#374151' : '#e2e8f0' },
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Custom Range panel */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase' }}>
                Custom Range
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500, mb: 0.5 }}>From</Typography>
                  <DatePicker
                    value={filters.dateFrom ? dayjs(filters.dateFrom) : null}
                    onChange={(v) => { onFilterChange('dateFrom', v ? v.format('YYYY-MM-DD') : ''); setQuickPreset(null) }}
                    format='DD/MM/YYYY'
                    minDate={overallMinDate || undefined}
                    maxDate={filters.dateTo ? dayjs(filters.dateTo) : overallMaxDate || undefined}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        placeholder: 'DD/MM/YYYY',
                        sx: datePickerFieldSx,
                      },
                      popper: { disableScrollLock: true },
                    }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500, mb: 0.5 }}>To</Typography>
                  <DatePicker
                    value={filters.dateTo ? dayjs(filters.dateTo) : null}
                    onChange={(v) => { onFilterChange('dateTo', v ? v.format('YYYY-MM-DD') : ''); setQuickPreset(null) }}
                    format='DD/MM/YYYY'
                    minDate={filters.dateFrom ? dayjs(filters.dateFrom) : overallMinDate || undefined}
                    maxDate={overallMaxDate || undefined}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        placeholder: 'DD/MM/YYYY',
                        sx: datePickerFieldSx,
                      },
                      popper: { disableScrollLock: true },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2, pt: 1.5, gap: 1 }}>
          <Button
            onClick={() => { onFiltersChange({ dateFrom: '', dateTo: '' }); setQuickPreset(null) }}
            size='small'
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              borderRadius: '6px',
              border: '1px solid #23252a',
              color: '#23252a',
              px: 2,
              '&:hover': { borderColor: '#23252a', color: '#23252a', backgroundColor: '#f1f5f9' },
            }}
          >
            Reset
          </Button>
          <Button
            onClick={() => setDateRangeAnchor(null)}
            variant='contained'
            size='small'
            disableElevation
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              borderRadius: '6px',
              backgroundColor: '#23252a',
              color: 'white',
              px: 2.5,
              '&:hover': { backgroundColor: '#374151' },
            }}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Active filter chips */}
      {(filters.countries?.length > 0 || selectedSources.length > 0 || filters.dateFrom || filters.dateTo) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
          {filters.countries.map((c) => (
            <Chip
              key={c}
              label={c}
              size='small'
              onDelete={() => onFilterChange('countries', filters.countries.filter((x) => x !== c))}
              sx={{ bgcolor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }}
            />
          ))}
          {selectedSources.map((src) => (
            <Chip
              key={src}
              label={`Source: ${src}`}
              size='small'
              onDelete={() => onFilterChange('source', selectedSources.filter((x) => x !== src))}
              sx={{ bgcolor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }}
            />
          ))}
          {filters.dateFrom && (
            <Chip
              label={`From: ${dayjs(filters.dateFrom).format('DD/MM/YYYY')}`}
              size='small'
              onDelete={() => onFilterChange('dateFrom', '')}
              sx={{ bgcolor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }}
            />
          )}
          {filters.dateTo && (
            <Chip
              label={`To: ${dayjs(filters.dateTo).format('DD/MM/YYYY')}`}
              size='small'
              onDelete={() => onFilterChange('dateTo', '')}
              sx={{ bgcolor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

export default NewsFiltersBar
